import { useCallback, useEffect, useState } from "react";

import {
  getSkuLogistics,
  getSkuUomMappings,
  getItemSyncErrors,
  getSyncRun,
  testItemMasterSync,
  triggerItemMasterSync,
} from "../../api/client.js";
import { getNextTableSort, useTableSort } from "../../hooks/useTableSort.js";
import {
  formatDetailNumber,
  formatDetailVolume,
  formatDetailWeight,
  formatOperationalNumber,
} from "../../utils/number-format.js";
import { Icon } from "../Icon.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import {
  CompactOverflowMenu,
  DataMasterActiveFilters,
  DataMasterDrawer,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterPagination,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
  SyncStatusStrip,
} from "./DataMasterLayout.jsx";

const DATA_STATUS_LABELS = {
  COMPLETE: "Lengkap",
  PARTIAL: "Sebagian",
  RATIO_NEEDS_REVIEW: "Perlu ditinjau",
  NO_UOM: "Tanpa UOM",
  SOURCE_INVALID: "Data sumber tidak valid",
};

const UOM_LEVEL_LABELS = {
  SMALL: "UOM terkecil",
  MIDDLE: "UOM tengah",
  LARGE: "UOM terbesar",
};

const SYNC_ACTIVE_STATUSES = new Set(["QUEUED", "RUNNING"]);

function statusLabel(value) {
  return DATA_STATUS_LABELS[value] || value || "Belum tersedia";
}

function uomLevelLabel(value) {
  return UOM_LEVEL_LABELS[value] || value || "-";
}

function formatTimestamp(value) {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function formatRawRatio(value) {
  return formatDetailNumber(value);
}

function latestSyncResult(syncRun) {
  if (!syncRun) return null;
  if (syncRun.result) return syncRun.result;

  try {
    return syncRun.result_json ? JSON.parse(syncRun.result_json) : null;
  } catch {
    return null;
  }
}

function syncStatusLabel(syncRun) {
  const status = syncRun?.status || "BELUM_ADA";
  if (SYNC_ACTIVE_STATUSES.has(status)) return "Sinkronisasi sedang berjalan";
  if (status === "SUCCESS") return "Sinkronisasi selesai";
  if (status === "PARTIAL_SUCCESS") return "Sinkronisasi selesai sebagian";
  if (status === "FAILED") return "Sinkronisasi gagal";
  return "Belum pernah sinkron";
}

function syncSourceLabel(syncRun) {
  return syncRun?.trigger_type === "SCHEDULED" ? "Terjadwal" : "Manual";
}

function itemSyncMetrics(latestSync) {
  const result = latestSyncResult(latestSync);

  return [
    { label: "sukses", value: result?.successCount ?? latestSync?.records_upserted ?? 0 },
    { label: "lengkap", value: result?.completeCount ?? 0 },
    { label: "sebagian", value: result?.partialCount ?? 0 },
    { label: "perlu ditinjau", value: result?.reviewCount ?? 0 },
    { label: "error", value: result?.errorCount ?? latestSync?.error_count ?? 0 },
  ];
}

function formatMetricValue(value) {
  return formatOperationalNumber(value, "0");
}

function nonNegativeCount(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.floor(parsed)
    : fallback;
}

function formatElapsedDuration(startedAt) {
  const started = Date.parse(startedAt || "");
  if (!Number.isFinite(started)) return "";

  const totalSeconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours} jam ${minutes} menit`;
  if (minutes > 0) return `${minutes} menit ${seconds} detik`;
  return `${seconds} detik`;
}

function itemSyncProgress(latestSync) {
  const result = latestSyncResult(latestSync);

  if (latestSync?.status === "PARTIAL_SUCCESS") {
    return [
      `${formatMetricValue(result?.successCount ?? latestSync?.records_upserted ?? 0)} SKU diproses`,
      `${formatMetricValue(result?.reviewCount ?? 0)} perlu ditinjau`,
      `${formatMetricValue(result?.errorCount ?? latestSync?.error_count ?? 0)} error`,
    ].join(" · ");
  }

  if (!SYNC_ACTIVE_STATUSES.has(latestSync?.status)) return "";

  const successCount = nonNegativeCount(
    result?.successCount,
    nonNegativeCount(latestSync?.records_upserted),
  );
  const errorCount = nonNegativeCount(
    result?.errorCount,
    nonNegativeCount(latestSync?.error_count),
  );
  const processedCount = nonNegativeCount(
    result?.processedCount,
    successCount + errorCount,
  );
  const expectedCount = nonNegativeCount(
    result?.expectedCount,
    nonNegativeCount(result?.totalCount, nonNegativeCount(latestSync?.records_seen)),
  );
  const pageCount = nonNegativeCount(result?.pageCount);
  const currentPage = Math.max(
    1,
    nonNegativeCount(result?.currentPage, nonNegativeCount(latestSync?.checkpoint_page, 1)),
  );
  const visiblePage = pageCount ? Math.min(currentPage, pageCount) : currentPage;
  const elapsed = formatElapsedDuration(
    result?.startedAt || latestSync?.started_at || latestSync?.created_at,
  );

  const parts = [
    `Memproses ${formatMetricValue(processedCount)} dari ${
      expectedCount ? formatMetricValue(expectedCount) : "?"
    } SKU`,
    `Halaman ${visiblePage} dari ${pageCount || "?"}`,
    `Berhasil ${formatMetricValue(successCount)} · Error ${formatMetricValue(errorCount)}`,
  ];

  if (elapsed) parts.push(`Berjalan ${elapsed}`);
  return parts.join(" · ");
}

function itemSyncMetricsV2(latestSync) {
  const result = latestSyncResult(latestSync);

  return [
    {
      label: "diproses",
      value: result?.processedCount ?? latestSync?.records_seen ?? 0,
    },
    { label: "lengkap", value: result?.completeCount ?? 0 },
    { label: "sebagian", value: result?.partialCount ?? 0 },
    { label: "perlu ditinjau", value: result?.reviewCount ?? 0 },
    {
      label: "error final",
      value:
        result?.finalErrorCount ??
        result?.errorCount ??
        latestSync?.error_count ??
        0,
    },
  ];
}

function itemSyncProgressV2(latestSync) {
  if (!latestSync) return "";
  const result = latestSyncResult(latestSync);
  const successCount = nonNegativeCount(
    result?.successCount,
    nonNegativeCount(latestSync?.records_upserted),
  );
  const errorCount = nonNegativeCount(
    result?.finalErrorCount ?? result?.errorCount,
    nonNegativeCount(latestSync?.error_count),
  );
  const processedCount = nonNegativeCount(
    result?.processedCount,
    successCount + errorCount,
  );
  const expectedCount = nonNegativeCount(
    result?.expectedCount,
    nonNegativeCount(latestSync?.expected_records),
  );
  const pageCount = nonNegativeCount(result?.pageCount);
  const currentPage = Math.max(
    1,
    nonNegativeCount(
      result?.currentPage,
      nonNegativeCount(latestSync?.checkpoint_page, 1),
    ),
  );
  const visiblePage = pageCount ? Math.min(currentPage, pageCount) : currentPage;
  const parts = [
    `${formatMetricValue(processedCount)} dari ${
      expectedCount ? formatMetricValue(expectedCount) : "?"
    } SKU diproses`,
    `Halaman ${visiblePage} dari ${pageCount || "?"}`,
    `Berhasil ${formatMetricValue(successCount)} · Error final ${formatMetricValue(errorCount)}`,
  ];

  if (SYNC_ACTIVE_STATUSES.has(latestSync.status)) {
    parts.unshift("Sinkronisasi berjalan di server. Halaman ini boleh ditutup.");
    const elapsed = formatElapsedDuration(
      result?.startedAt || latestSync?.started_at || latestSync?.created_at,
    );
    if (elapsed) parts.push(`Berjalan ${elapsed}`);
  }

  return parts.join(" · ");
}

function StatusBadge({ status }) {
  return (
    <span className={`status-badge status-badge--${String(status || "unknown").toLowerCase()}`}>
      {statusLabel(status)}
    </span>
  );
}

function UomPath({ row }) {
  const units = [row.uomSmall, row.uomMiddle, row.uomLarge].filter(Boolean);
  if (!units.length) return <span className="muted-inline">Belum tersedia</span>;

  return (
    <span title={units.join(" / ")}>
      {units.join(" → ")}
    </span>
  );
}

function SkuDetailDrawer({ item, onClose }) {
  if (!item) return null;

  const detailRows = [
    ["Principal", item.principal],
    ["Accurate Item ID", item.accurateItemId],
    ["Raw Unit 1", item.rawUnit1],
    ["Raw Unit 2", item.rawUnit2],
    ["Raw Unit 3", item.rawUnit3],
    ["Raw Rasio Tengah", formatRawRatio(item.rawRatio2)],
    ["Raw Rasio Besar", formatRawRatio(item.rawRatio3)],
    ["Factor Tengah ke UOM Terkecil", formatDetailNumber(item.factorMiddleToSmall)],
    ["Factor Besar ke UOM Terkecil", formatDetailNumber(item.factorLargeToSmall)],
    ["Derivasi Tengah", item.middleFactorDerivation],
    ["Derivasi Besar", item.largeFactorDerivation],
    ["Panjang", formatDetailNumber(item.lengthCm)],
    ["Lebar", formatDetailNumber(item.widthCm)],
    ["Tinggi", formatDetailNumber(item.heightCm)],
    ["Berat per UOM", [item.weightSmallKg, item.weightMiddleKg, item.weightLargeKg].map(formatDetailWeight).join(" / ")],
    ["Volume per UOM", [item.volumeSmallM3, item.volumeMiddleM3, item.volumeLargeM3].map(formatDetailVolume).join(" / ")],
    ["Terakhir Sinkron", formatTimestamp(item.lastSyncedAt)],
  ];

  return (
    <DataMasterDrawer onClose={onClose} title={`Detail SKU ${item.skuCode}`}>
      <div className="dm-drawer__body sku-detail-drawer">
        <header className="sku-detail-drawer__summary">
          <div>
            <h3>{item.itemName}</h3>
            <p>{item.skuCode}</p>
          </div>
          <StatusBadge status={item.dataStatus} />
        </header>

        <dl className="sku-readonly-detail__grid">
          {detailRows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value || "-"}</dd>
            </div>
          ))}
        </dl>

        {item.validationMessages?.length > 0 && (
          <section className="sku-detail-drawer__warnings">
            <h4>Validasi</h4>
            <ul>
              {item.validationMessages.map((message, index) => (
                <li key={`${message.code || message.message}-${index}`}>
                  {message.message || message.code}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </DataMasterDrawer>
  );
}

function ItemSyncStrip({
  latestFullSync,
  latestSync,
  onRunDiagnostic,
  onSync,
  running,
  testing,
}) {
  const isRunning = SYNC_ACTIVE_STATUSES.has(latestSync?.status);
  const finalErrorCount = nonNegativeCount(
    latestSyncResult(latestSync)?.finalErrorCount ??
      latestSyncResult(latestSync)?.errorCount ??
      latestSync?.error_count,
  );
  const [errorRows, setErrorRows] = useState([]);
  const [errorDrawerOpen, setErrorDrawerOpen] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);

  async function openErrors() {
    if (!latestSync?.id || finalErrorCount === 0) return;
    setErrorDrawerOpen(true);
    setErrorLoading(true);
    try {
      const payload = await getItemSyncErrors(latestSync.id, { pageSize: 100 });
      setErrorRows(payload.errors || []);
    } finally {
      setErrorLoading(false);
    }
  }

  return (
    <>
      <SyncStatusStrip
      actions={
        <>
          {finalErrorCount > 0 && (
            <button className="text-button" onClick={openErrors} type="button">
              Lihat error final
            </button>
          )}
          <CompactOverflowMenu
            actions={[
              {
                disabled: running || testing || isRunning,
                label: testing ? "Menguji..." : "Tes Koneksi Item",
                onClick: onRunDiagnostic,
              },
            ]}
          />
          <button
            className="primary-button"
            disabled={running || testing || isRunning}
            onClick={onSync}
            type="button"
          >
            <Icon name="sync" size={15} />
            {running || isRunning ? "Sinkronisasi berjalan" : "Sinkronkan Item Accurate"}
          </button>
        </>
      }
      lastSynced={formatTimestamp(
        isRunning
          ? latestSync?.last_progress_at || latestSync?.updated_at
          : latestSync?.completed_at || latestSync?.updated_at,
      )}
      lastSyncedLabel={isRunning ? "Progress terakhir" : "Terakhir Sinkron"}
      metrics={itemSyncMetricsV2(latestSync)}
      progress={itemSyncProgressV2(latestSync)}
      status={`${syncStatusLabel(latestSync)} · ${syncSourceLabel(latestSync)}${
        latestFullSync?.completed_at
          ? ` · Full sukses: ${formatTimestamp(latestFullSync.completed_at)}`
          : ""
      }`}
      title="Sumber: Accurate · Hanya-baca"
      />

      {errorDrawerOpen && (
        <DataMasterDrawer
          onClose={() => setErrorDrawerOpen(false)}
          title="Error final sinkronisasi Item"
        >
          <div className="dm-drawer__body item-sync-errors">
            {errorLoading ? (
              <p>Memuat detail error...</p>
            ) : errorRows.length === 0 ? (
              <p>Tidak ada error final pada run ini.</p>
            ) : (
              <ul>
                {errorRows.map((row) => (
                  <li key={`${row.recordKey}-${row.code}`}>
                    <strong>{row.recordLabel || row.recordKey}</strong>
                    <span>{row.category} · {row.attempts} percobaan</span>
                    <p>{row.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DataMasterDrawer>
      )}
    </>
  );
}

export function SkuLogisticsTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [latestSync, setLatestSync] = useState(null);
  const [latestFullSync, setLatestFullSync] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [filters, setFilters] = useState({
    activeStatus: "",
    category: "",
    dataStatus: "",
    search: "",
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { sort, setSort } = useTableSort("skuCode");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await getSkuLogistics({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sort.key,
        sortDir: sort.direction,
        ...filters,
      });

      setRows(payload.items || []);
      setSummary(payload.summary || {});
      setCategories(payload.categories || []);
      setLatestSync(payload.latestSync || null);
      setLatestFullSync(payload.latestFullSync || null);
      setPagination((current) => ({
        ...current,
        ...(payload.pagination || {}),
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, sort]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!latestSync?.id || !SYNC_ACTIVE_STATUSES.has(latestSync.status)) {
      return undefined;
    }

    const timer = window.setInterval(async () => {
      try {
        const payload = await getSyncRun(latestSync.id);
        setLatestSync(payload.syncRun);

        if (!SYNC_ACTIVE_STATUSES.has(payload.syncRun?.status)) {
          setSyncing(false);
          await load();
        }
      } catch {
        // Keep existing data visible if polling fails.
      }
    }, 2500);

    return () => window.clearInterval(timer);
  }, [latestSync?.id, latestSync?.status, load]);

  async function runSync() {
    setSyncing(true);
    setMessage("");
    setError("");

    try {
      const payload = await triggerItemMasterSync();
      setLatestSync(payload.syncRun);
      setMessage(payload.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSyncing(false);
    }
  }

  async function runDiagnostic() {
    setTesting(true);
    setMessage("");
    setError("");

    try {
      const payload = await testItemMasterSync();
      setMessage(
        payload.itemFound
          ? `Tes koneksi berhasil: ${formatMetricValue(payload.metadata?.all?.rowCount)} item (${formatMetricValue(payload.metadata?.active?.rowCount)} aktif, ${formatMetricValue(payload.metadata?.inactive?.rowCount)} nonaktif)`
          : payload.message,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setTesting(false);
    }
  }

  function submitFilters(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters({
      search: String(formData.get("search") || "").trim(),
      dataStatus: String(formData.get("dataStatus") || ""),
      category: String(formData.get("category") || ""),
      activeStatus: String(formData.get("activeStatus") || ""),
    });
  }

  function clearFilters() {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters({ search: "", dataStatus: "", category: "", activeStatus: "" });
  }

  function toggleSort(column) {
    setPagination((current) => ({ ...current, page: 1 }));
    setSort((current) => getNextTableSort(current, column));
  }

  return (
    <DataMasterSection
      error={error}
      message={message}
      subtitle="Tinjau kelengkapan struktur UOM, berat, dan volume hasil sinkronisasi Accurate Item Master."
      title="SKU Logistics"
    >
      <DataMasterKpiStrip label="Ringkasan kesiapan data logistik">
        <DataMasterKpiCard
          helper="Seluruh item tersimpan"
          icon="database"
          label="Total SKU"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Memiliki minimal satu satuan"
          icon="check"
          label="SKU dengan UOM"
          tone="success"
          value={summary.withUom}
        />
        <DataMasterKpiCard
          helper="Sebagian atribut tersedia"
          icon="partial"
          label="Sebagian"
          tone="warning"
          value={summary.partial}
        />
        <DataMasterKpiCard
          helper="Perlu pemeriksaan data sumber"
          icon="warning"
          label="Perlu ditinjau"
          tone="danger"
          value={summary.review}
        />
        <DataMasterKpiCard
          helper="Siap dihitung otomatis"
          icon="orders"
          label="SKU lengkap"
          tone="success"
          value={summary.complete}
        />
      </DataMasterKpiStrip>

      <ItemSyncStrip
        latestFullSync={latestFullSync}
        latestSync={latestSync}
        onRunDiagnostic={runDiagnostic}
        onSync={runSync}
        running={syncing}
        testing={testing}
      />

      <DataMasterToolbar className="dm-toolbar--sku" onSubmit={submitFilters}>
        <Icon name="search" size={15} />
        <input
          key={`search-${filters.search}`}
          defaultValue={filters.search}
          name="search"
          placeholder="Cari kode, nama barang, atau principal"
        />
        <select
          key={`status-${filters.dataStatus}`}
          defaultValue={filters.dataStatus}
          name="dataStatus"
        >
          <option value="">Semua status data</option>
          {Object.entries(DATA_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          key={`category-${filters.category}`}
          defaultValue={filters.category}
          name="category"
        >
          <option value="">Semua kategori</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <select
          key={`active-${filters.activeStatus}`}
          defaultValue={filters.activeStatus}
          name="activeStatus"
        >
          <option value="">Semua status aktif</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <button className="primary-button" disabled={loading} type="submit">
          <Icon name="filter" size={14} />
          Terapkan
        </button>
        <button
          className="text-button"
          disabled={loading}
          onClick={clearFilters}
          type="button"
        >
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Status", value: DATA_STATUS_LABELS[filters.dataStatus] },
          { label: "Kategori", value: filters.category },
          {
            label: "Aktivitas",
            value:
              filters.activeStatus === "ACTIVE"
                ? "Aktif"
                : filters.activeStatus === "INACTIVE"
                  ? "Nonaktif"
                  : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--sku dm-table-shell--accurate"
        emptyText="Tidak ada SKU Logistics yang sesuai dengan filter aktif."
        loading={loading}
        rowCount={rows.length}
      >
        <table className="master-table master-table--sku-logistics dm-table--fixed">
          <colgroup>
            <col className="dm-col-item" />
            <col className="dm-col-name" />
            <col className="dm-col-uom-path" />
            <col className="dm-col-logistics" />
            <col className="dm-col-status-wide" />
            <col className="dm-col-date" />
          </colgroup>
          <thead>
            <tr>
              <SortableHeader column="skuCode" sort={sort} toggleSort={toggleSort}>
                Barang
              </SortableHeader>
              <SortableHeader column="categoryName" sort={sort} toggleSort={toggleSort}>
                Kategori
              </SortableHeader>
              <SortableHeader column="uomSmall" sort={sort} toggleSort={toggleSort}>
                Struktur UOM
              </SortableHeader>
              <th>Logistik UOM terbesar</th>
              <SortableHeader column="dataStatus" sort={sort} toggleSort={toggleSort}>
                Status data
              </SortableHeader>
              <SortableHeader column="lastSyncedAt" sort={sort} toggleSort={toggleSort}>
                Terakhir sinkron
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                className="clickable-table-row"
                key={row.id}
                onClick={() => setSelectedItem(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedItem(row);
                  }
                }}
                tabIndex={0}
              >
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.itemName}</strong>
                    <span>{row.skuCode}</span>
                  </div>
                </td>
                <td>{row.categoryName || "—"}</td>
                <td
                  title={[row.uomSmall, row.uomMiddle, row.uomLarge]
                    .filter(Boolean)
                    .join(" / ")}
                >
                  <UomPath row={row} />
                </td>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--logistics">
                    <strong>{formatDetailWeight(row.weightLargeKg)}</strong>
                    <span>{formatDetailVolume(row.volumeLargeM3)}</span>
                  </div>
                </td>
                <td>
                  <StatusBadge status={row.dataStatus} />
                </td>
                <td>{formatTimestamp(row.lastSyncedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={(page) =>
          setPagination((current) => ({ ...current, page }))
        }
        onPageSizeChange={(pageSize) =>
          setPagination((current) => ({ ...current, page: 1, pageSize }))
        }
        pagination={pagination}
      />

      <SkuDetailDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </DataMasterSection>
  );
}

export function SkuUomMappingsTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [latestSync, setLatestSync] = useState(null);
  const [latestFullSync, setLatestFullSync] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [filters, setFilters] = useState({
    activeStatus: "",
    dataStatus: "",
    search: "",
    uomLevel: "",
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const { sort, setSort } = useTableSort("skuCode");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await getSkuUomMappings({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sort.key,
        sortDir: sort.direction,
        ...filters,
      });

      setRows(payload.mappings || []);
      setSummary(payload.summary || {});
      setLatestSync(payload.latestSync || null);
      setLatestFullSync(payload.latestFullSync || null);
      setPagination((current) => ({
        ...current,
        ...(payload.pagination || {}),
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, sort]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!latestSync?.id || !SYNC_ACTIVE_STATUSES.has(latestSync.status)) {
      return undefined;
    }

    const timer = window.setInterval(async () => {
      try {
        const payload = await getSyncRun(latestSync.id);
        setLatestSync(payload.syncRun);

        if (!SYNC_ACTIVE_STATUSES.has(payload.syncRun?.status)) {
          setSyncing(false);
          await load();
        }
      } catch {
        // Keep existing data visible if polling fails.
      }
    }, 2500);

    return () => window.clearInterval(timer);
  }, [latestSync?.id, latestSync?.status, load]);

  async function runSync() {
    setSyncing(true);
    setMessage("");
    setError("");

    try {
      const payload = await triggerItemMasterSync();
      setLatestSync(payload.syncRun);
      setMessage(payload.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSyncing(false);
    }
  }

  async function runDiagnostic() {
    setTesting(true);
    setMessage("");
    setError("");

    try {
      const payload = await testItemMasterSync();
      setMessage(
        payload.itemFound
          ? `Tes koneksi berhasil: ${formatMetricValue(payload.metadata?.all?.rowCount)} item (${formatMetricValue(payload.metadata?.active?.rowCount)} aktif, ${formatMetricValue(payload.metadata?.inactive?.rowCount)} nonaktif)`
          : payload.message,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setTesting(false);
    }
  }

  function submitFilters(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters({
      search: String(formData.get("search") || "").trim(),
      uomLevel: String(formData.get("uomLevel") || ""),
      dataStatus: String(formData.get("dataStatus") || ""),
      activeStatus: String(formData.get("activeStatus") || ""),
    });
  }

  function clearFilters() {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters({ search: "", uomLevel: "", dataStatus: "", activeStatus: "" });
  }

  function toggleSort(column) {
    setPagination((current) => ({ ...current, page: 1 }));
    setSort((current) => getNextTableSort(current, column));
  }

  return (
    <DataMasterSection
      error={error}
      message={message}
      subtitle="Tinjau struktur satuan per SKU, faktor konversi, serta berat dan volume tiap UOM."
      title="Struktur UOM"
    >
      <DataMasterKpiStrip label="Ringkasan struktur satuan">
        <DataMasterKpiCard
          helper="Seluruh item dari Accurate"
          icon="database"
          label="SKU tersinkron"
          value={summary.syncedSku}
        />
        <DataMasterKpiCard
          helper="Memiliki minimal satu mapping"
          icon="orders"
          label="SKU dengan mapping"
          tone="info"
          value={summary.mappedSku}
        />
        <DataMasterKpiCard
          helper="Seluruh level satuan tersimpan"
          icon="partial"
          label="Total mapping UOM"
          tone="warning"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Cek rasio atau sumber data"
          icon="truck"
          label="SKU perlu ditinjau"
          tone="warning"
          value={summary.reviewSku}
        />
        <DataMasterKpiCard
          helper="Tetap tersedia di SKU Logistics"
          icon="check"
          label="SKU tanpa UOM"
          tone="danger"
          value={summary.noUom}
        />
      </DataMasterKpiStrip>

      <ItemSyncStrip
        latestFullSync={latestFullSync}
        latestSync={latestSync}
        onRunDiagnostic={runDiagnostic}
        onSync={runSync}
        running={syncing}
        testing={testing}
      />

      <DataMasterToolbar className="dm-toolbar--uom" onSubmit={submitFilters}>
        <Icon name="search" size={15} />
        <input
          key={`search-${filters.search}`}
          defaultValue={filters.search}
          name="search"
          placeholder="Cari SKU, nama barang, atau satuan"
        />
        <select
          key={`level-${filters.uomLevel}`}
          defaultValue={filters.uomLevel}
          name="uomLevel"
        >
          <option value="">Semua level UOM</option>
          {Object.entries(UOM_LEVEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          key={`status-${filters.dataStatus}`}
          defaultValue={filters.dataStatus}
          name="dataStatus"
        >
          <option value="">Semua status data</option>
          {Object.entries(DATA_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          key={`active-${filters.activeStatus}`}
          defaultValue={filters.activeStatus}
          name="activeStatus"
        >
          <option value="">Semua status aktif</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <button className="primary-button" disabled={loading} type="submit">
          <Icon name="filter" size={14} />
          Terapkan
        </button>
        <button
          className="text-button"
          disabled={loading}
          onClick={clearFilters}
          type="button"
        >
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Level", value: UOM_LEVEL_LABELS[filters.uomLevel] },
          { label: "Status", value: DATA_STATUS_LABELS[filters.dataStatus] },
          {
            label: "Aktivitas",
            value:
              filters.activeStatus === "ACTIVE"
                ? "Aktif"
                : filters.activeStatus === "INACTIVE"
                  ? "Nonaktif"
                  : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--uom dm-table-shell--accurate"
        emptyText="Tidak ada Struktur UOM yang sesuai dengan filter aktif."
        loading={loading}
        rowCount={rows.length}
      >
        <table className="master-table master-table--uom-structure dm-table--fixed">
          <colgroup>
            <col className="dm-col-item" />
            <col className="dm-col-uom" />
            <col className="dm-col-factor" />
            <col className="dm-col-number" />
            <col className="dm-col-number" />
            <col className="dm-col-status-wide" />
            <col className="dm-col-date" />
          </colgroup>
          <thead>
            <tr>
              <SortableHeader column="skuCode" sort={sort} toggleSort={toggleSort}>
                Barang
              </SortableHeader>
              <SortableHeader column="uomName" sort={sort} toggleSort={toggleSort}>
                Satuan
              </SortableHeader>
              <SortableHeader column="factorToSmall" sort={sort} toggleSort={toggleSort}>
                Level & faktor
              </SortableHeader>
              <SortableHeader column="weightPerUomKg" sort={sort} toggleSort={toggleSort}>
                Berat per UOM
              </SortableHeader>
              <SortableHeader column="volumePerUomM3" sort={sort} toggleSort={toggleSort}>
                Volume per UOM
              </SortableHeader>
              <SortableHeader column="dataStatus" sort={sort} toggleSort={toggleSort}>
                Status data
              </SortableHeader>
              <SortableHeader column="lastSyncedAt" sort={sort} toggleSort={toggleSort}>
                Terakhir sinkron
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.itemName}</strong>
                    <span>{row.skuCode}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{row.uomName}</strong>
                    {row.isSingleUom ? (
                      <span className="dm-inline-pill">Satu-satunya UOM</span>
                    ) : null}
                  </div>
                </td>
                <td title={row.factorDerivation || ""}>
                  <div className="dm-cell-stack">
                    <strong>{uomLevelLabel(row.uomLevel)}</strong>
                    <span>
                      Faktor: {formatDetailNumber(row.factorToSmall)}
                    </span>
                  </div>
                </td>
                <td>{formatDetailWeight(row.weightPerUomKg)}</td>
                <td>{formatDetailVolume(row.volumePerUomM3)}</td>
                <td>
                  <StatusBadge status={row.dataStatus} />
                </td>
                <td>{formatTimestamp(row.lastSyncedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={(page) =>
          setPagination((current) => ({ ...current, page }))
        }
        onPageSizeChange={(pageSize) =>
          setPagination((current) => ({ ...current, page: 1, pageSize }))
        }
        pagination={pagination}
      />
    </DataMasterSection>
  );
}
